// IGK-2P v2.1 Phase-3 — CD bench as a guided 3-STEP WIZARD (modal, portalled).
// Step 1 framing (feedback composer: Fix framing opens a correction box BEFORE
// firing, so a retry steers instead of re-rolling) · Step 2 colour (plain badge;
// legacy/unmeasured frames say so honestly) · Step 3 lock. Optional CD note on
// EVERY verdict writes to the audit trail. Raw numbers / audit / feedback history
// live behind a Details expander. Portalled to <body> (true full-screen overlay).
import React, { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { X, Lock, Unlock, Check, RefreshCw, AlertTriangle, Loader2, ShieldCheck, Maximize2, ZoomIn, ZoomOut, ChevronDown, ChevronRight, ArrowLeft, Info, Sparkles } from 'lucide-react';
import { toast } from '../../utils/toast';
import { regenIGK2P, lockShot, unlockShot, getLockAudit, getVideoReadUrl, resolveLook, addVerdictNote, getVerdictHistory } from './igk2pApi';

const resolveUrl = (url) => (!url ? '' : url.startsWith('http') ? url : `${window.__API_BASE}${url}`);

function Thumb({ src, label, onZoom }) {
  return (
    <figure className="min-w-0">
      {src ? (
        <button type="button" onClick={() => onZoom?.(src, label)}
          className="group relative block w-full" title={`Click to zoom — ${label}`}
          data-testid={`igk2p-thumb-${label.replace(/\s+/g, '-')}`}>
          <img src={src} alt={label} className="w-full aspect-video object-contain bg-black/40 rounded-lg border border-white/10 group-hover:border-fuchsia-400/50 transition-colors" />
          <span className="absolute top-1 right-1 opacity-0 group-hover:opacity-100 transition-opacity bg-black/60 rounded p-0.5"><Maximize2 className="w-3.5 h-3.5 text-white/80" /></span>
        </button>
      ) : (
        <div className="w-full aspect-video rounded-lg bg-white/[0.03] border border-dashed border-white/10" />
      )}
      <figcaption className="text-[9px] text-white/40 text-center mt-1 uppercase tracking-wider">{label}</figcaption>
    </figure>
  );
}

const STEPS = [{ n: 1, title: 'Framing' }, { n: 2, title: 'Colour' }, { n: 3, title: 'Lock' }];

export default function Igk2pCdBench({ shot, onClose, onReload }) {
  const [busy, setBusy] = useState(false);
  const [audit, setAudit] = useState([]);
  const [history, setHistory] = useState([]);
  const [readUrl, setReadUrl] = useState(null);
  const [unlockReason, setUnlockReason] = useState('');
  const [showUnlock, setShowUnlock] = useState(false);
  const [showDetails, setShowDetails] = useState(false);
  const [showEvidence, setShowEvidence] = useState(false);
  const [step, setStep] = useState(1);
  const [cdNote, setCdNote] = useState('');            // optional note on any verdict
  const [showFix, setShowFix] = useState(false);       // Fix-framing composer open
  const [correction, setCorrection] = useState('');    // targeted-retry text
  const [zoom, setZoom] = useState(null);
  const [zoomScale, setZoomScale] = useState(1);
  const openZoom = (src, label) => { setZoom({ src, label }); setZoomScale(1); };

  const locked = !!shot?.igk2p_lock;
  const look = resolveLook(shot?.scene_number);

  const blueprint = resolveUrl(shot?.blueprint_url);
  const pass1 = resolveUrl(shot?.keyframe_hyperreal_pre_vlm_url || shot?.keyframe_neutral_url || shot?.keyframe_hyperreal_url);
  const logMaster = resolveUrl(shot?.keyframe_hyperreal_slog3_s3_url || shot?.keyframe_hyperreal_slog3_url);
  const graded = resolveUrl(shot?.keyframe_hyperreal_s3_url || shot?.keyframe_hyperreal_url);
  const hasKf = !!(shot?.keyframe_hyperreal_url || shot?.keyframe_hyperreal_s3_url);
  const hasLog = !!(shot?.keyframe_hyperreal_slog3_url || shot?.keyframe_hyperreal_slog3_s3_url);

  const j1 = shot?.keyframe_hyperreal_audit || shot?.sketch_audit || null;
  const j1Score = shot?.vlm_qa_score ?? shot?.keyframe_qa_score;
  const j1Elements = (shot?.keyframe_igk2p_judge1 || {}).elements || [];
  const mismatches = shot?.keyframe_igk2p_brief_blueprint_mismatch || [];
  const j1Sentence = (typeof j1 === 'string' && j1.trim())
    ? j1.trim().replace(/\s+/g, ' ').slice(0, 200)
    : (hasKf
      ? 'Framing reads consistent with the approved lineart. Measures pass; the AI note is advisory only.'
      : 'No framing check on record yet — generate the frame first.');

  // Colour verdict — honest about missing measurement (CD item 3).
  const measured = hasLog;
  const conformOOT = shot?.keyframe_igk2p_conform_within_tolerance === false;
  const colourBadge = !measured
    ? { label: 'not measured — legacy frame', tone: 'grey' }
    : conformOOT
      ? { label: 'off — check (grade out of tolerance)', tone: 'warn' }
      : { label: 'colour matches scene reference', tone: 'ok' };

  useEffect(() => {
    getLockAudit(shot.id).then((r) => setAudit(r.audit || [])).catch(() => {});
    getVideoReadUrl(shot.id).then(setReadUrl).catch(() => {});
    getVerdictHistory(shot.id).then((r) => setHistory(r.history || [])).catch(() => {});
  }, [shot.id]);

  useEffect(() => {
    const onKey = (e) => { if (e.key !== 'Escape') return; if (zoom) setZoom(null); else onClose?.(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [zoom, onClose]);

  const act = async (fn, okMsg) => {
    setBusy(true);
    try { const r = await fn(); if (okMsg) toast.success(okMsg); onReload?.(); return r; }
    catch (e) { toast.error(e.response?.data?.detail || 'Action failed'); }
    finally { setBusy(false); }
  };

  // Log a verdict + optional CD note to the audit trail, then optionally advance.
  const logVerdict = async (verdict, { correction_prompt = '', advanceTo = null } = {}) => {
    try {
      await addVerdictNote(shot.id, { verdict, note: cdNote.trim(), correction_prompt, by: 'cd' });
      getVerdictHistory(shot.id).then((r) => setHistory(r.history || [])).catch(() => {});
    } catch (e) { /* audit is best-effort; never block the verdict */ }
    setCdNote('');
    if (advanceTo) setStep(advanceTo);
  };

  const fireFixFraming = async () => {
    const text = correction.trim();
    if (!text) return;
    await act(() => regenIGK2P(shot.id, { allow_missing_sketch: true, allow_stale_bindings: true, allow_empty_canon: true, correction_prompt: text }), 'Targeted retry queued — steering with your note');
    await logVerdict('fix_framing', { correction_prompt: text });
    setShowFix(false); setCorrection('');
  };

  const generateLegacy = () => act(
    () => regenIGK2P(shot.id, { allow_missing_sketch: true, allow_stale_bindings: true, allow_empty_canon: true }),
    'IGK-2P generation queued');

  const Stepper = () => (
    <div className="flex items-center gap-2 mb-4" data-testid="igk2p-wizard-stepper">
      {STEPS.map((s, i) => {
        const state = step === s.n ? 'current' : step > s.n ? 'done' : 'todo';
        return (
          <React.Fragment key={s.n}>
            <div className="flex items-center gap-1.5">
              <span className={`w-5 h-5 rounded-full flex items-center justify-center text-[10px] font-bold ${state === 'current' ? 'bg-fuchsia-500 text-white' : state === 'done' ? 'bg-emerald-500/25 text-emerald-300' : 'bg-white/10 text-white/40'}`}>
                {state === 'done' ? <Check className="w-3 h-3" /> : s.n}
              </span>
              <span className={`text-[11px] font-medium ${state === 'current' ? 'text-white' : 'text-white/40'}`}>{s.title}</span>
            </div>
            {i < STEPS.length - 1 && <span className="flex-1 h-px bg-white/10" />}
          </React.Fragment>
        );
      })}
    </div>
  );

  const btn = 'inline-flex items-center justify-center gap-1.5 px-4 py-2 rounded-lg text-[12px] font-semibold transition-colors disabled:opacity-40';

  // Shared optional-note field for verdicts.
  const NoteField = () => (
    <input value={cdNote} onChange={(e) => setCdNote(e.target.value)} placeholder="Optional CD note (logged with your verdict)…"
      data-testid="igk2p-cd-note" className="w-full mb-2 text-[11px] px-2 py-1.5 bg-black/40 border border-white/15 rounded-md text-white outline-none focus:border-fuchsia-400/50" />
  );

  return createPortal((
    <div className="fixed inset-0 z-[200] bg-black/80 backdrop-blur-sm flex items-center justify-center p-4" data-testid={`igk2p-cd-bench-${shot.id}`} onClick={onClose}>
      <div className="w-full max-w-3xl max-h-[92vh] overflow-y-auto rounded-2xl border border-white/10 bg-[#0c0c10] p-5" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-4">
          <div>
            <div className="text-sm font-semibold text-white">Review · {shot?.scene_number}-{shot?.shot_number}</div>
            <div className="text-[10px] text-white/40">Guided review · lock-means-lock · look {look}</div>
          </div>
          <button onClick={onClose} data-testid="igk2p-bench-close" className="text-white/40 hover:text-white"><X className="w-5 h-5" /></button>
        </div>

        {locked && (
          <div className="mb-4 flex items-center gap-2 px-3 py-2 rounded-lg bg-emerald-500/10 border border-emerald-400/30 text-emerald-200 text-[11px]" data-testid={`igk2p-locked-banner-${shot.id}`}>
            <ShieldCheck className="w-4 h-4" /> Locked — video reads a frozen frame. Regeneration is refused until you unlock.
          </div>
        )}

        <Stepper />

        {/* STEP 1 · FRAMING */}
        {step === 1 && (
          <div data-testid="igk2p-wizard-step-1">
            <h3 className="text-[15px] font-semibold text-white mb-3">Does the frame match the approved lineart?</h3>
            <div className="grid grid-cols-3 gap-3 items-start">
              <div className="col-span-2"><Thumb src={pass1} label="the frame" onZoom={openZoom} /></div>
              <div className="col-span-1"><Thumb src={blueprint} label="approved lineart" onZoom={openZoom} /></div>
            </div>
            <div className="mt-3 rounded-lg bg-white/5 border border-white/10 p-3">
              <div className="flex items-center justify-between mb-1">
                <div className="text-[9px] uppercase tracking-wider text-white/40">What the check found</div>
                {(j1Elements.length > 0 || mismatches.length > 0) && (
                  <button data-testid="igk2p-evidence-toggle" onClick={() => setShowEvidence((v) => !v)} className="inline-flex items-center gap-1 text-[10px] text-fuchsia-300/80 hover:text-fuchsia-200">
                    <Info className="w-3 h-3" /> Evidence
                  </button>
                )}
              </div>
              <p className="text-[12px] text-white/80 leading-relaxed" data-testid="igk2p-j1-sentence">{j1Sentence}</p>
              {showEvidence && (
                <div className="mt-2 pt-2 border-t border-white/10 space-y-1" data-testid="igk2p-evidence-popover">
                  {mismatches.map((m, i) => <div key={`m${i}`} className="text-[10px] text-amber-200/80">• brief↔lineart: {String(m).slice(0, 160)}</div>)}
                  {j1Elements.slice(0, 6).map((el, i) => <div key={`e${i}`} className="text-[10px] text-white/55">• {String(el.item || el).slice(0, 160)}</div>)}
                  {mismatches.length === 0 && j1Elements.length === 0 && <div className="text-[10px] text-white/40">No structured evidence recorded.</div>}
                </div>
              )}
            </div>

            {showFix ? (
              <div className="mt-4" data-testid="igk2p-fix-composer">
                <label className="text-[11px] text-white/60 mb-1 block">Tell it what to fix <span className="text-white/35">(this steers the retry — e.g. "basket further left, camera lower")</span></label>
                <textarea value={correction} onChange={(e) => setCorrection(e.target.value)} rows={2} autoFocus
                  data-testid="igk2p-fix-textarea" placeholder="tell it what to fix"
                  className="w-full text-[12px] px-2 py-1.5 bg-black/40 border border-white/15 rounded-md text-white outline-none focus:border-amber-400/50 resize-none" />
                <div className="flex items-center gap-2 mt-2">
                  <button data-testid={`igk2p-fix-submit-${shot.id}`} disabled={busy || !correction.trim()} onClick={fireFixFraming}
                    className={`${btn} bg-amber-500/25 text-amber-100 hover:bg-amber-500/40`}>{busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />} Send fix & retry</button>
                  <button onClick={() => { setShowFix(false); setCorrection(''); }} className="text-[11px] text-white/40 hover:text-white/70">Cancel</button>
                </div>
              </div>
            ) : (
              <div className="mt-4">
                <NoteField />
                <div className="flex items-center gap-2">
                  <button data-testid={`igk2p-looks-right-${shot.id}`} disabled={busy} onClick={() => logVerdict('looks_right', { advanceTo: 2 })}
                    className={`${btn} bg-emerald-500/25 text-emerald-100 hover:bg-emerald-500/40`}><Check className="w-4 h-4" /> Looks right</button>
                  <button data-testid={`igk2p-fix-framing-${shot.id}`} disabled={busy || locked} onClick={() => setShowFix(true)}
                    className={`${btn} bg-amber-500/15 text-amber-200 hover:bg-amber-500/25`}><RefreshCw className="w-4 h-4" /> Fix framing</button>
                  <button data-testid={`igk2p-overrule-${shot.id}`} disabled={busy} onClick={() => logVerdict('overrule', { advanceTo: 2 })}
                    className={`${btn} bg-white/5 text-white/60 hover:bg-white/10`}><AlertTriangle className="w-4 h-4" /> Overrule</button>
                </div>
              </div>
            )}
          </div>
        )}

        {/* STEP 2 · COLOUR */}
        {step === 2 && (
          <div data-testid="igk2p-wizard-step-2">
            <h3 className="text-[15px] font-semibold text-white mb-3">Is the colour right for this scene?</h3>
            {!hasLog ? (
              <div className="rounded-lg bg-white/[0.03] border border-dashed border-white/15 p-5 text-center" data-testid="igk2p-legacy-colour-empty">
                <p className="text-[12px] text-white/70 mb-3">No log master — this is a legacy frame. Generate through IGK-2P to enable grading review.</p>
                <button data-testid={`igk2p-generate-igk2p-${shot.id}`} disabled={busy} onClick={generateLegacy}
                  className={`${btn} mx-auto bg-fuchsia-500/25 text-fuchsia-100 hover:bg-fuchsia-500/40`}>{busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4" />} Generate (IGK-2P)</button>
              </div>
            ) : (
              <div className="grid grid-cols-2 gap-3">
                <Thumb src={logMaster} label="log master" onZoom={openZoom} />
                <Thumb src={graded} label="graded" onZoom={openZoom} />
              </div>
            )}
            <div className="mt-3 flex items-center gap-2 flex-wrap">
              <span className="px-2 py-1 rounded-md bg-fuchsia-500/15 text-fuchsia-200 border border-fuchsia-400/30 text-[11px]" data-testid={`igk2p-scene-look-${shot.id}`}>Scene look · {look}</span>
              <span data-testid="igk2p-colour-badge" className={`px-2 py-1 rounded-md text-[11px] font-medium border ${
                colourBadge.tone === 'ok' ? 'bg-emerald-500/15 text-emerald-200 border-emerald-400/30'
                : colourBadge.tone === 'warn' ? 'bg-amber-500/15 text-amber-200 border-amber-400/30'
                : 'bg-white/8 text-white/55 border-white/20'}`}>
                {colourBadge.tone === 'ok' ? '✓ ' : colourBadge.tone === 'warn' ? '⚠ ' : '• '}{colourBadge.label}
              </span>
            </div>
            <div className="mt-4">
              {hasLog && <NoteField />}
              <div className="flex items-center gap-2">
                <button data-testid={`igk2p-approve-look-${shot.id}`} disabled={busy || !hasLog} onClick={() => logVerdict('approve_look', { advanceTo: 3 })}
                  className={`${btn} bg-emerald-500/25 text-emerald-100 hover:bg-emerald-500/40`}><Check className="w-4 h-4" /> Approve look</button>
                <button data-testid={`igk2p-change-look-${shot.id}`} disabled={busy || !hasLog} onClick={() => { logVerdict('change_look'); toast.message('Change the scene look from the Blueprint Board · Scene Look picker (re-grade, zero regen).'); }}
                  className={`${btn} bg-white/5 text-white/70 hover:bg-white/10`}><RefreshCw className="w-4 h-4" /> Change look</button>
                <button data-testid="igk2p-back-step2" onClick={() => setStep(1)} className="ml-auto inline-flex items-center gap-1 text-[11px] text-white/40 hover:text-white/70"><ArrowLeft className="w-3.5 h-3.5" /> Back</button>
              </div>
              {!hasLog && <p className="mt-2 text-[10px] text-white/40">Colour verdict disabled — nothing measured on a legacy frame.</p>}
            </div>
          </div>
        )}

        {/* STEP 3 · LOCK */}
        {step === 3 && (
          <div data-testid="igk2p-wizard-step-3">
            <h3 className="text-[15px] font-semibold text-white mb-3">Lock it?</h3>
            <div className="grid grid-cols-2 gap-3 mb-3">
              <Thumb src={graded} label="final frame" onZoom={openZoom} />
              <div className="flex flex-col justify-center gap-2 text-[11px] text-white/60">
                <div>Framing: <span className="text-emerald-300">accepted</span></div>
                <div>Colour: <span className={measured ? (conformOOT ? 'text-amber-300' : 'text-emerald-300') : 'text-white/50'}>{measured ? (conformOOT ? 'out of tolerance' : 'approved') : 'not measured (legacy)'}</span></div>
                <div>Video reads: <span className="font-mono text-white/70">{readUrl ? (readUrl.locked ? 'FROZEN' : 'live') : '…'}</span></div>
              </div>
            </div>
            <p className="text-[12px] text-white/70 mb-2">Locking freezes this frame for video. Unlocking is logged.</p>
            {!hasLog && (
              <div className="mb-3 flex items-center gap-2 px-3 py-2 rounded-lg bg-amber-500/10 border border-amber-400/30 text-amber-200 text-[11px]" data-testid="igk2p-legacy-lock-note">
                <AlertTriangle className="w-4 h-4" /> Locking a legacy frame — no graded master attached.
              </div>
            )}
            {locked ? (
              <div>
                <button data-testid={`igk2p-unlock-btn-${shot.id}`} disabled={busy} onClick={() => setShowUnlock((v) => !v)}
                  className={`${btn} w-full bg-amber-500/20 text-amber-100 hover:bg-amber-500/30`}><Unlock className="w-4 h-4" /> Unlock (logged)</button>
                {showUnlock && (
                  <div className="mt-2 flex items-center gap-2" data-testid={`igk2p-unlock-form-${shot.id}`}>
                    <input value={unlockReason} onChange={(e) => setUnlockReason(e.target.value)} placeholder="Reason for unlock (logged)…"
                      className="flex-1 text-[11px] px-2 py-1.5 bg-black/40 border border-white/15 rounded-md text-white outline-none focus:border-amber-400/50" data-testid={`igk2p-unlock-reason-${shot.id}`} />
                    <button disabled={busy || !unlockReason.trim()} onClick={() => act(() => unlockShot(shot.id, unlockReason.trim()), 'Unlocked + logged').then(() => { setShowUnlock(false); setUnlockReason(''); })}
                      className="px-3 py-1.5 rounded-md bg-amber-500/25 text-amber-100 text-[11px] disabled:opacity-40" data-testid={`igk2p-unlock-confirm-${shot.id}`}>Confirm</button>
                  </div>
                )}
              </div>
            ) : (
              <button data-testid={`igk2p-lock-btn-${shot.id}`} disabled={busy} onClick={() => act(() => lockShot(shot.id), 'Shot locked')}
                className={`${btn} w-full bg-emerald-500/25 text-emerald-100 hover:bg-emerald-500/40`}>{busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Lock className="w-4 h-4" />} Lock shot</button>
            )}
            <button data-testid="igk2p-back-step3" onClick={() => setStep(2)} className="mt-3 inline-flex items-center gap-1 text-[11px] text-white/40 hover:text-white/70"><ArrowLeft className="w-3.5 h-3.5" /> Back</button>
          </div>
        )}

        {/* DETAILS EXPANDER */}
        <div className="mt-5 border-t border-white/10 pt-3">
          <button data-testid="igk2p-details-toggle" onClick={() => setShowDetails((v) => !v)}
            className="flex items-center gap-1.5 text-[11px] text-white/50 hover:text-white/80">
            {showDetails ? <ChevronDown className="w-3.5 h-3.5" /> : <ChevronRight className="w-3.5 h-3.5" />} Details (numbers · audit · feedback history)
          </button>
          {showDetails && (
            <div className="mt-3 space-y-3 text-[10px]" data-testid="igk2p-details-panel">
              <div className="rounded-md bg-white/5 border border-white/10 p-2.5">
                <div className="text-[9px] uppercase tracking-wider text-white/40 mb-1">Measures</div>
                <div className="grid grid-cols-2 gap-x-4 gap-y-1 text-white/70 font-mono">
                  <span>Judge-1 QA: {j1Score != null ? j1Score : '—'}</span>
                  <span>LUT: {look}.cube (33-pt)</span>
                  <span>content-lock: {hasLog ? '✓' : '— (legacy)'}</span>
                  <span>palette ΔE: {shot?.igk2p_palette_de ?? (measured ? '≈0' : 'not measured')}</span>
                  <span>black floor: {shot?.igk2p_black_floor ?? (measured ? '≈0.093' : '—')}</span>
                  <span>white: {shot?.igk2p_white ?? (measured ? '≈0.596' : '—')}</span>
                </div>
              </div>
              {history.length > 0 && (
                <div className="rounded-md bg-white/5 border border-white/10 p-2.5" data-testid={`igk2p-feedback-history-${shot.id}`}>
                  <div className="text-[9px] uppercase tracking-wider text-white/40 mb-1">Feedback history</div>
                  {history.slice(0, 8).map((h, i) => (
                    <div key={i} className="text-white/60">
                      <span className="text-fuchsia-300/70">{h.verdict}</span> by {h.by} · {String(h.at).slice(0, 16)}
                      {h.correction_prompt ? <span className="text-amber-200/70"> · fix: “{h.correction_prompt.slice(0, 60)}”</span> : null}
                      {h.note ? <span className="text-white/50"> · note: {h.note.slice(0, 60)}</span> : null}
                    </div>
                  ))}
                </div>
              )}
              {audit.length > 0 && (
                <div className="rounded-md bg-white/5 border border-white/10 p-2.5" data-testid={`igk2p-audit-trail-${shot.id}`}>
                  <div className="text-[9px] uppercase tracking-wider text-white/40 mb-1">Unlock audit</div>
                  {audit.slice(0, 5).map((a, i) => <div key={i} className="text-white/60">unlock by {a.by} · {a.reason || 'no reason'} · {String(a.at).slice(0, 16)}</div>)}
                </div>
              )}
            </div>
          )}
        </div>

        {zoom && createPortal((
          <div className="fixed inset-0 z-[240] bg-black/95 flex flex-col" data-testid="igk2p-zoom-viewer" onClick={(e) => { e.stopPropagation(); setZoom(null); }}>
            <div className="flex items-center justify-between px-4 py-2 text-white/80" onClick={(e) => e.stopPropagation()}>
              <span className="text-[11px] font-semibold uppercase tracking-wider">{zoom.label}</span>
              <div className="flex items-center gap-2">
                <button data-testid="igk2p-zoom-out" onClick={() => setZoomScale((s) => Math.max(1, +(s - 0.5).toFixed(2)))} className="p-1 rounded hover:bg-white/10"><ZoomOut className="w-4 h-4" /></button>
                <span className="text-[10px] w-10 text-center tabular-nums">{Math.round(zoomScale * 100)}%</span>
                <button data-testid="igk2p-zoom-in" onClick={() => setZoomScale((s) => Math.min(5, +(s + 0.5).toFixed(2)))} className="p-1 rounded hover:bg-white/10"><ZoomIn className="w-4 h-4" /></button>
                <button data-testid="igk2p-zoom-close" onClick={() => setZoom(null)} className="p-1 rounded hover:bg-white/10"><X className="w-4 h-4" /></button>
              </div>
            </div>
            <div className="flex-1 overflow-auto flex items-center justify-center p-4" onClick={(e) => e.stopPropagation()}>
              <img src={zoom.src} alt={zoom.label} style={{ transform: `scale(${zoomScale})` }}
                onClick={() => setZoomScale((s) => (s >= 3 ? 1 : +(s + 1).toFixed(2)))}
                className="max-w-full max-h-full object-contain transition-transform origin-center cursor-zoom-in" />
            </div>
          </div>
        ), document.body)}
      </div>
    </div>
  ), document.body);
}
